import { parsePhoneNumberFromString } from 'libphonenumber-js';

export type RegistrationSessionValues = {
  email: string;
  firstName: string;
  lastName: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  phoneCountryDialCode: string;
  phoneNumber: string;
};

export type UnregisteredUser = {
  email: string;
  firstName: string;
  lastName: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  phone: string | null;
};

export const REGISTRATION_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const REGISTRATION_PHONE_NUMBER_REGEX = /^\+\d{1,4} \d{4,14}$/;

export const MAX_SOCIAL_HANDLE_LENGTH = 2048;

export const normalizeRegistrationString = (value: string): string =>
  value.trim();

export const normalizeRegistrationEmail = (email: string): string =>
  normalizeRegistrationString(email).toLowerCase();

export const isRegistrationEmail = (email: string): boolean => {
  const normalizedEmail = normalizeRegistrationEmail(email);
  return REGISTRATION_EMAIL_REGEX.test(normalizedEmail);
};

const capSocialHandle = (value: string): string =>
  value.length > MAX_SOCIAL_HANDLE_LENGTH
    ? value.slice(0, MAX_SOCIAL_HANDLE_LENGTH)
    : value;

// Mirror of `creator-api/src/social-enrichment/ig/instagram-handle.util.ts`.
export const normalizeRegistrationInstagram = (instagram: string): string => {
  const normalized = instagram
    .trim()
    .replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/^@/, '')
    .replace(/\/+$/, '')
    .toLowerCase();

  return capSocialHandle(normalized);
};

// Mirror of `creator-api/src/social-enrichment/tt/tiktok-handle.util.ts`.
export const normalizeRegistrationTiktok = (tiktok: string): string => {
  const normalized = tiktok
    .trim()
    .replace(/^(https?:\/\/)?(www\.)?(m\.)?tiktok\.com\//i, '')
    .replace(/[?#].*$/, '')
    .replace(/^@/, '')
    .replace(/\/.*$/, '')
    .replace(/\/+$/, '')
    .toLowerCase();

  return capSocialHandle(normalized);
};

// Mirror of `creator-api/src/social-enrichment/yt/youtube-handle.util.ts`.
export const normalizeRegistrationYoutube = (youtube: string): string => {
  const trimmed = youtube.trim();
  if (!trimmed) {
    return '';
  }

  const withoutQuery = trimmed.replace(/[?#].*$/, '').replace(/\/+$/, '');
  const withoutHost = withoutQuery.replace(
    /^(https?:\/\/)?(www\.)?(m\.)?(youtube\.com|youtu\.be)\//i,
    '',
  );
  const normalized = withoutHost
    .replace(/^@/, '')
    .replace(/^(channel|c|user)\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();

  return capSocialHandle(normalized);
};

const normalizeDialCode = (dialCode: string): string => {
  const onlyDigits = dialCode.replace(/\D/g, '');
  if (!onlyDigits) {
    return '+1';
  }

  return `+${onlyDigits}`;
};

export const buildRegistrationPhoneNumber = (
  dialCode: string,
  number: string,
): string | null => {
  const onlyDigits = number.replace(/\D/g, '');
  if (!onlyDigits) {
    return null;
  }

  const normalizedDialCode = normalizeDialCode(dialCode);
  const fullNumber = `${normalizedDialCode}${onlyDigits}`;
  const parsed = parsePhoneNumberFromString(fullNumber);
  if (!parsed || !parsed.isValid()) {
    return null;
  }

  if (parsed.nationalNumber !== onlyDigits) {
    return null;
  }

  return `${normalizedDialCode} ${onlyDigits}`;
};

export const toUnregisteredUser = (
  values: RegistrationSessionValues,
): UnregisteredUser => {
  return {
    email: normalizeRegistrationEmail(values.email),
    firstName: normalizeRegistrationString(values.firstName),
    lastName: normalizeRegistrationString(values.lastName),
    instagram: normalizeRegistrationInstagram(values.instagram),
    tiktok: normalizeRegistrationTiktok(values.tiktok),
    youtube: normalizeRegistrationYoutube(values.youtube),
    phone: buildRegistrationPhoneNumber(
      values.phoneCountryDialCode,
      values.phoneNumber,
    ),
  };
};

type DashboardRedirectDecision = 'none' | 'home';

type ResolveDashboardRedirectInput = {
  isLoggedIn: boolean;
};

export const resolveDashboardRedirect = ({
  isLoggedIn,
}: ResolveDashboardRedirectInput): DashboardRedirectDecision => {
  if (isLoggedIn) {
    return 'home';
  }

  return 'none';
};

export * from './registration.steps.model';
